/**
 * Canonical GA4 event names for new instrumentation.
 *
 * When adding instrumentation:
 * 1. Add a constant here (snake_case value).
 * 2. Extend `AnalyticsEventParamsMap` in `analytics.types.d.ts` if the event has a fixed schema.
 * 3. Call `analyticsService.track(AnalyticsEventCatalog.YOUR_EVENT, { ... })` from services or bridges — not from React components directly.
 *
 * Legacy bridges under `features/analytics` still use `constants/events.js` until migrated.
 */
export const AnalyticsEventCatalog = Object.freeze({
  LOGIN: "login",
  LOGOUT: "logout",
  SIGNUP_COMPLETED: "signup_completed",
  ACCOUNT_DELETED: "account_deleted",

  ROOM_CREATED: "room_created",
  ROOM_JOINED: "room_joined",
  ROOM_ARCHIVED: "room_archived",

  TOURNAMENT_CREATED: "tournament_created",
  TOURNAMENT_STARTED: "tournament_started",
  TOURNAMENT_COMPLETED: "tournament_completed",
  COMPETITION_CREATED: "competition_created",
  COMPETITION_PUBLISHED: "competition_published",
  COMPETITION_STARTED: "competition_started",
  COMPETITION_COMPLETED: "competition_completed",

  ORGANIZER_GUIDANCE_SHOWN: "organizer_guidance_shown",
  ORGANIZER_GUIDANCE_PRIMARY_CLICK: "organizer_guidance_primary_click",
  ORGANIZER_GUIDANCE_SECONDARY_CLICK: "organizer_guidance_secondary_click",
  ORGANIZER_GUIDANCE_ATTENTION_CLICK: "organizer_guidance_attention_click",
  ORGANIZER_GUIDANCE_PHASE_VIEW: "organizer_guidance_phase_view",

  PROFILE_CREATED: "profile_created",
  PROFILE_COMPLETED: "profile_completed",
  PROFILE_UPDATED: "profile_updated",

  INVITE_LINK_CREATED: "invite_link_created",
  INVITE_QR_GENERATED: "invite_qr_generated",
  INVITE_JOIN_COMPLETED: "invite_join_completed",

  APP_OPEN: "app_open",
  PWA_INSTALL: "pwa_install",
  APP_UPDATE: "app_update",
  OFFLINE_MODE_ENTERED: "offline_mode_entered",
  OFFLINE_MODE_EXITED: "offline_mode_exited",

  MATCH_CREATED: "match_created",
  MATCH_JOINED: "match_joined",
  MATCH_STARTED: "match_started",
  WINNER_DECLARED: "winner_declared",
  MATCH_COMPLETED: "match_completed",
  MATCH_ABANDONED: "match_abandoned",

  SHARE_LINK: "share_link",
  SHARE_QR: "share_qr",
  SHARE_NATIVE: "share_native",
  SPECTATOR_OPENED: "spectator_opened",

  UNDO_USED: "undo_used",
  INTERVAL_STARTED: "interval_started",
  GAME_COMPLETED: "game_completed",
  MATCH_POINT: "match_point",
  GAME_POINT: "game_point",

  COURT_SETUP_COMPLETED: "court_setup_completed",
  COURT_SWAPPED: "court_swapped",

  INSTALL_PROMPT_SHOWN: "install_prompt_shown",
  INSTALL_PROMPT_DISMISSED: "install_prompt_dismissed",
  INSTALL_COMPLETED: "install_completed",
  UPDATE_AVAILABLE: "update_available",
  UPDATE_INSTALLED: "update_installed",

  FAILED_ROOM_JOIN: "failed_room_join",
  SYNC_CONFLICT: "sync_conflict",
  FIRESTORE_PERMISSION_DENIED: "firestore_permission_denied",
  OFFLINE_SYNC_FAILED: "offline_sync_failed",

  /** GA4 recommended screen tracking event (usually via `trackScreen`). */
  SCREEN_VIEW: "screen_view",
});

/**
 * Screen names for `trackScreen` (firebase_screen / firebase_screen_class).
 * Prefer these over ad-hoc strings for consistent funnels.
 */
export const AnalyticsScreenCatalog = Object.freeze({
  WELCOME: "welcome",
  HOME: "home",
  LOBBY: "lobby",
  MATCH_COURT: "match_court",
  MATCH_SETUP: "match_setup",
  REGISTRY: "registry",
  STATS: "stats",
  MATCH_HISTORY: "match_history",
  TOURNAMENT: "tournament",
  PROFILE: "profile",
  PROFILE_MATCHES: "profile_matches",
  PROFILE_ROOMS: "profile_rooms",
  FEEDBACK: "feedback",
  TV_DISPLAY: "tv_display",
  SIGN_IN: "sign_in",
});
